import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import api from "@/lib/api";
import { useTier } from "@/context/TierContext";
import { getPlanCta } from "@/lib/planCta";
import { Button } from "@/components/ui/button";
import {
  Crown, Sparkles, X, ArrowRight, Lock, Building2, FileText, Scale, Gavel, Palette, Vault, ClipboardCheck, Wrench, DollarSign, MessagesSquare, CalendarClock, ListChecks,
} from "lucide-react";

// Single source of truth — add a premium feature here and both the welcome
// moment and the hub pick it up with no other change.
const PREMIUM_FEATURES = [
  { key: "monthly_checkup", spotlight: "monthly_checkup", icon: ClipboardCheck, title: "Monthly Rental Checkup",
    oneLiner: "Timestamped monthly condition snapshots during your lease.",
    route: (pid) => (pid ? `/property/${pid}?tab=during` : "/start") },
  { key: "maintenance", spotlight: "maintenance", icon: Wrench, title: "Maintenance Follow-Up",
    oneLiner: "Track repairs to resolution with an evidence trail & follow-up drafts.",
    route: (pid) => (pid ? `/property/${pid}?tab=during` : "/start") },
  { key: "rent_proof", spotlight: "rent_proof", icon: DollarSign, title: "Rent Payment Record",
    oneLiner: "A timestamped record of the rent payments you log.",
    route: (pid) => (pid ? `/property/${pid}?tab=during` : "/start") },
  { key: "communications", spotlight: "communications", icon: MessagesSquare, title: "Communication Timeline",
    oneLiner: "Log landlord communications; they export into your evidence package.",
    route: (pid) => (pid ? `/property/${pid}?tab=during` : "/start") },
  { key: "moveout_prep", spotlight: "moveout_prep", icon: ListChecks, title: "Move-Out Prep Plan",
    oneLiner: "AI suggests things to review — clean, patch, re-photograph or ask about — before move-out.",
    route: (pid) => (pid ? `/property/${pid}?tab=moveout` : "/start") },
  { key: "moveout_plan", spotlight: "moveout_plan", icon: CalendarClock, title: "Move-Out Countdown Plan",
    oneLiner: "A guided runway from lease-end to full move-out documentation.",
    route: (pid) => (pid ? `/property/${pid}?tab=moveout` : "/start") },
  { key: "unlimited", icon: Building2, title: "Unlimited properties",
    oneLiner: "Document every rental you've ever lived in.", route: () => "/start" },
  { key: "evidence_bundle", icon: FileText, title: "Evidence bundle PDF",
    oneLiner: "Export a professional report with a full photo appendix.",
    route: (pid) => (pid ? `/property/${pid}` : "/start") },
  { key: "dispute_package", icon: Scale, title: "Dispute preparation",
    oneLiner: "Deduction auditor, an editable dispute-letter draft & exhibit appendix.",
    route: (pid) => (pid ? `/properties/${pid}/dispute-letter` : "/start") },
  { key: "escalation_packet", icon: Gavel, title: "Escalation Documentation Packet",
    oneLiner: "Timeline, labeled exhibits, chain-of-custody summary & an editable demand-letter draft.",
    route: (pid) => (pid ? `/properties/${pid}/small-claims` : "/start") },
  { key: "report_styling", icon: Palette, title: "Report styling",
    oneLiner: "Accent color, cover photo & a custom footer on your PDFs.",
    route: () => "/settings#report-styling" },
  { key: "vault", icon: Vault, title: "Unlimited document vault",
    oneLiner: "Store leases, receipts & notices with no limits.",
    route: (pid) => (pid ? `/property/${pid}` : "/start") },
];

export function PremiumSection({ firstPropertyId }) {
  const navigate = useNavigate();
  const { isPremium, showPremiumWelcome, spotlights, trialEligible, trialing, openUpgrade } = useTier();
  const planCta = getPlanCta({ trialEligible, trialing });
  const hubRef = useRef(null);
  const [welcomeVisible, setWelcomeVisible] = useState(false);
  const [newKeys, setNewKeys] = useState([]);
  const seededRef = useRef(false);
  const spotSeededRef = useRef(false);

  // One-time welcome: fire nudge-seen on first render so it never nags twice
  // (server flag persists across devices), but keep it visible until dismissed.
  useEffect(() => {
    if (isPremium && showPremiumWelcome && !seededRef.current) {
      seededRef.current = true;
      setWelcomeVisible(true);
      api.post("/users/nudge-seen", { nudge: "premium_welcome" }).catch(() => {});
    }
  }, [isPremium, showPremiumWelcome]);

  // Feature spotlights ("what's new"): show a New badge once per feature, then mark seen.
  useEffect(() => {
    if (isPremium && spotlights?.length && !spotSeededRef.current) {
      spotSeededRef.current = true;
      setNewKeys(spotlights);
      spotlights.forEach((k) => api.post("/users/nudge-seen", { nudge: `spotlight_${k}` }).catch(() => {}));
    }
  }, [isPremium, spotlights]);

  const explore = () => hubRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });

  return (
    <div className="mt-4 space-y-4">
      <AnimatePresence>
        {isPremium && welcomeVisible && (
          <motion.div
            initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}
            data-testid="premium-welcome-panel"
            className="relative overflow-hidden rounded-2xl border-2 border-amber-border bg-gradient-to-br from-amber-soft to-white p-5 sm:p-6"
          >
            <button data-testid="premium-welcome-dismiss" onClick={() => setWelcomeVisible(false)}
              aria-label="Dismiss" className="absolute right-3 top-3 grid place-items-center h-8 w-8 rounded-full text-amber-ink/70 hover:bg-white/70 hover:text-amber-ink transition-colors">
              <X size={16} />
            </button>
            <div className="flex items-center gap-2">
              <Crown size={22} className="text-amber-ink" />
              <h2 className="font-heading text-xl sm:text-2xl font-bold text-navy">You're on TenantSeal Plus — here's what just unlocked</h2>
            </div>
            <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-2.5">
              {PREMIUM_FEATURES.map((f) => (
                <div key={f.key} data-testid={`premium-welcome-item-${f.key}`} className="flex items-start gap-2.5 rounded-xl bg-white/70 border border-amber-border/60 px-3.5 py-2.5">
                  <f.icon size={17} className="text-brand mt-0.5 shrink-0" />
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-navy">{f.title}</p>
                    <p className="text-xs text-[#64748B]">{f.oneLiner}</p>
                  </div>
                </div>
              ))}
            </div>
            <Button data-testid="premium-welcome-explore-btn" onClick={explore}
              className="mt-5 rounded-xl h-11 px-6 bg-navy hover:bg-navy-hover text-white font-semibold">
              Explore TenantSeal Plus tools <ArrowRight size={16} className="ml-2" />
            </Button>
          </motion.div>
        )}
      </AnimatePresence>

      <div ref={hubRef} data-testid="premium-hub" className="scroll-mt-24 rounded-2xl border border-[#E2E8F0] bg-white p-5 sm:p-6">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <Crown size={20} className="text-amber-ink" />
            <h2 className="font-heading text-lg font-bold text-navy">TenantSeal Plus</h2>
          </div>
          {isPremium ? (
            <span data-testid="premium-hub-active-badge" className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold border border-amber-border bg-amber-soft text-amber-ink">
              <Sparkles size={12} /> Active
            </span>
          ) : (
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border border-[#E2E8F0] bg-[#F8FAFC] text-[#64748B]">
              <Lock size={12} /> Locked
            </span>
          )}
        </div>
        <p className="mt-1 text-sm text-[#64748B]">
          {isPremium ? "Jump straight into your premium tools." : "Everything you get when you upgrade to Premium."}
        </p>

        <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
          {PREMIUM_FEATURES.map((f) => {
            if (isPremium) {
              return (
                <button key={f.key} data-testid={`premium-hub-card-${f.key}`}
                  onClick={() => navigate(f.route(firstPropertyId))}
                  className="group flex items-start gap-3 rounded-xl border border-[#E2E8F0] hover:border-brand hover:bg-brand/5 px-4 py-3 text-left transition-colors">
                  <span className="grid place-items-center h-9 w-9 rounded-lg bg-brand/10 text-brand shrink-0"><f.icon size={17} /></span>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-navy flex items-center gap-1.5">
                      {f.title}
                      {f.spotlight && newKeys.includes(f.spotlight) && (
                        <span data-testid={`premium-hub-new-${f.key}`} className="inline-flex items-center rounded-full bg-brand text-white text-[10px] font-bold px-1.5 py-0.5 leading-none">NEW</span>
                      )}
                    </p>
                    <p className="text-xs text-[#94A3B8] truncate">{f.oneLiner}</p>
                  </div>
                  <ArrowRight size={15} className="text-[#CBD5E1] group-hover:text-brand shrink-0 mt-1" />
                </button>
              );
            }
            return (
              <div key={f.key} data-testid={`premium-hub-card-locked-${f.key}`}
                className="flex items-start gap-3 rounded-xl border border-[#E2E8F0] bg-[#F8FAFC] px-4 py-3 opacity-80">
                <span className="grid place-items-center h-9 w-9 rounded-lg bg-[#F1F5F9] text-[#94A3B8] shrink-0"><Lock size={16} /></span>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold text-[#64748B]">{f.title}</p>
                  <p className="text-xs text-[#94A3B8] truncate">{f.oneLiner}</p>
                </div>
              </div>
            );
          })}
        </div>

        {!isPremium && (
          <Button data-testid="premium-hub-upgrade-btn" onClick={() => openUpgrade("premium")}
            className="mt-4 w-full h-11 rounded-xl bg-navy hover:bg-navy-hover text-white font-semibold">
            <Sparkles size={16} className="mr-2" /> {planCta.cta}
          </Button>
        )}
      </div>
    </div>
  );
}
